import { TFunction } from 'i18next';
import { THEME_VARIABLE } from '@/config';
import { ClaimStatusSlug, ClaimType } from '@/types';
import dayjs from '@/dayjsSetup';

// ─── Status tag display data ──────────────────────────────────────────────────

/**
 * Maps a ClaimStatusSlug to Ant Design tag color and icon color.
 * Follows the same pattern as getTicketStatusTagData in ticketStatus.ts.
 */
export function getClaimStatusTagData(slug: ClaimStatusSlug): {
  tagColor: string;
  iconColor: string;
} {
  switch (slug) {
    case 'pending':
      return {
        tagColor: 'warning',
        iconColor: THEME_VARIABLE?.customOrange ?? '#fa8c16',
      };
    case 'under-review':
      return {
        tagColor: 'processing',
        iconColor: THEME_VARIABLE?.customBlue ?? '#1677ff',
      };
    case 'approved':
      return {
        tagColor: 'cyan',
        iconColor: THEME_VARIABLE?.customMint ?? '#00c7be',
      };
    case 'rejected':
      return {
        tagColor: 'error',
        iconColor: THEME_VARIABLE?.customRed ?? '#dc3545',
      };
    case 'compensated':
      return {
        tagColor: 'success',
        iconColor: THEME_VARIABLE?.customGreen ?? '#2bb45d',
      };
    case 'compensation-failed':
      return {
        tagColor: 'error',
        iconColor: THEME_VARIABLE?.customRed ?? '#dc3545',
      };
    case 'closed':
      return {
        tagColor: 'default',
        iconColor: THEME_VARIABLE?.customGrayShade5 ?? '#939496',
      };
    default:
      return {
        tagColor: 'default',
        iconColor: THEME_VARIABLE?.customGrayShade5 ?? '#939496',
      };
  }
}

// ─── Label helpers ────────────────────────────────────────────────────────────

/**
 * Returns a translated label for a ClaimType.
 * Falls back to the raw type string if the translation key is missing.
 */
export function getClaimTypeLabel(type: ClaimType, t: TFunction): string {
  return String(t(`claim_type.${type}`, { defaultValue: type }));
}

/**
 * Returns a translated label for a ClaimStatusSlug.
 * Falls back to the raw slug if the translation key is missing.
 */
export function getClaimStatusLabel(slug: ClaimStatusSlug, t: TFunction): string {
  return String(t(`claim_status.${slug}`, { defaultValue: slug }));
}

// ─── Deadline formatter ───────────────────────────────────────────────────────

/**
 * Formats a claim window deadline into a human-readable remaining-time string.
 *
 * - Null / invalid input → '—'
 * - Past deadline        → translated 'Expired' string
 * - Future deadline      → e.g. '47 hours remaining'
 *
 * Treats input as UTC, compares against current UTC time.
 */
export function formatClaimDeadline(deadline: string | null, t: TFunction): string {
  if (!deadline) return '—';

  const deadlineDate = dayjs.utc(deadline);
  if (!deadlineDate.isValid()) return '—';

  const now = dayjs.utc();

  if (!deadlineDate.isAfter(now)) {
    return String(t('claim_deadline_expired', { defaultValue: 'Expired' }));
  }

  const hoursRemaining = deadlineDate.diff(now, 'hour');
  if (hoursRemaining < 1) {
    const minutesRemaining = deadlineDate.diff(now, 'minute');
    return String(
      t('claim_deadline_minutes_remaining', {
        count: Math.max(1, minutesRemaining),
        defaultValue: `${Math.max(1, minutesRemaining)} minutes remaining`,
      }),
    );
  }

  return String(
    t('claim_deadline_hours_remaining', {
      count: hoursRemaining,
      defaultValue: `${hoursRemaining} hours remaining`,
    }),
  );
}

// ─── Currency formatter ───────────────────────────────────────────────────────

/**
 * Formats an IQD amount with thousands separator.
 * Null / undefined amounts return '—'.
 *
 * @example formatIQD(50000) → '50,000 IQD'
 */
export function formatIQD(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return '—';
  return `${amount.toLocaleString('en-US')} IQD`;
}

// ─── Terminal status check ────────────────────────────────────────────────────

/**
 * Returns true when the claim has reached a terminal state —
 * i.e. no further merchant action is possible.
 */
export function isClaimTerminal(slug: ClaimStatusSlug): boolean {
  const terminalSlugs: ClaimStatusSlug[] = ['rejected', 'closed', 'compensated'];
  return terminalSlugs.includes(slug);
}

// ─── Eligibility reason message ───────────────────────────────────────────────

/**
 * Maps a machine-readable eligibility reason from the API to a translated
 * user-facing message.  Unknown reasons fall back to a generic message.
 */
export function getEligibilityReasonMessage(reason: string | null, t: TFunction): string {
  if (!reason) return '';

  const knownReasons: Record<string, string> = {
    claim_window_expired: String(
      t('claim_eligibility_reason.claim_window_expired', {
        defaultValue: 'The claim window for this order has expired.',
      }),
    ),
    order_not_rto_delivered: String(
      t('claim_eligibility_reason.order_not_rto_delivered', {
        defaultValue: 'Claims can only be filed after the order has been returned (RTO delivered).',
      }),
    ),
    // Path A reason (May 2026): order is at rto-received-warehouse but it
    // travelled through a carrier — the natural RTO chain hasn't completed
    // yet. Tell the merchant to wait for rto-delivered.
    order_not_returned_yet: String(
      t('claim_eligibility_reason.order_not_returned_yet', {
        defaultValue:
          'The parcel has not been returned to you yet. You can file a claim once the order is marked RTO Delivered.',
      }),
    ),
    claim_already_exists: String(
      t('claim_eligibility_reason.claim_already_exists', {
        defaultValue: 'A claim has already been filed for this order.',
      }),
    ),
  };

  return (
    knownReasons[reason] ??
    String(
      t('claim_eligibility_reason.unknown', {
        defaultValue: 'This order is not eligible for a claim.',
      }),
    )
  );
}
